/*
 **	SFC Calibration (c) 2026 Silver Falls Capital. All Rights Reserved
 **
 **	This code is unlicensed and may not be copied, used, modified,
 **	compiled, run, or distributed in any form without the explicit
 **	written permission of Silver Falls Capital.
 **
 **/

import { useState, useEffect } from "react"
import { Outlet, useOutletContext, Navigate } from "react-router-dom"
import { useMsal, useIsAuthenticated } from "@azure/msal-react"
import { acquireToken, REDIRECT_IN_PROGRESS } from "@sfc/shared/auth"

import type { ApplicationContextType } from "../components/common/types"
import LoadingPage from "./LoadingPage"
import CalibrationLayout from "./CalibrationLayout"
import { Alert } from "../components/common/Alert"

import Config from "../components/common/Config"
import { loginRequest } from "../lib/authConfig"
import { logger } from "../utils/logger"

type applicationType = {
  context: {
    application: ApplicationContextType
    logout: Function
  }
}

export function useContext() {
  return useOutletContext<applicationType>()
}

export default function AuthenticationState(): JSX.Element {
  const [context, setContext] = useState<ApplicationContextType | null>(null)
  const [authError, setAuthError] = useState<string | null>(null)

  const { instance, accounts, inProgress } = useMsal()
  const isAuthenticated = useIsAuthenticated()
  const isLoading = inProgress !== "none"

  function onLogout(): void {
    setContext(null)
    instance.logoutRedirect({ postLogoutRedirectUri: window.location.origin })
  }

  // Acquire the API access token once, then build the application context.
  //
  // acquireToken (from @sfc/shared) tries silently first and falls back to a
  // full-page redirect, with a loop breaker. Do not replace it with a bare
  // acquireTokenSilent — naive redirect fallback loops in production.
  useEffect(() => {
    if (!isAuthenticated || context) return

    const account = accounts[0] ?? instance.getActiveAccount()
    if (!account) return

    let cancelled = false

    acquireToken(instance, { ...loginRequest, account })
      .then(({ accessToken }) => {
        if (cancelled) return
        setContext({
          environment: Config.ENVIRONMENT,
          userToken: accessToken,
          userProfile: {
            name: account.name || account.username,
            email: account.username,
            userId: account.localAccountId,
          },
        })
      })
      .catch((error: any) => {
        // The helper is navigating away to re-authenticate; keep the spinner up.
        if (cancelled || error?.message === REDIRECT_IN_PROGRESS) return
        logger.error("Main: Failed to acquire access token:", error)
        // This previously only logged, leaving the user on a blank page.
        setAuthError(
          "Could not complete sign-in. Please reload the page, or contact support if this persists."
        )
      })

    return () => {
      cancelled = true
    }
  }, [isAuthenticated, context, accounts, instance])

  if (!isAuthenticated && !isLoading) {
    return <Navigate to="/" replace />
  }

  if (authError) {
    return (
      <div style={{ maxWidth: "42rem", margin: "4rem auto", padding: "0 1rem" }}>
        <Alert type="error" message="Sign-in failed" description={authError} showIcon={true} />
      </div>
    )
  }

  if (!context) {
    return <LoadingPage />
  }

  return (
    <CalibrationLayout context={context} onLogout={onLogout}>
      <Outlet
        context={
          { context: { application: context, logout: onLogout } } satisfies applicationType
        }
      />
    </CalibrationLayout>
  )
}
